import React, { useEffect, useState, useRef } from 'react'
import { Box, TextField, Typography, CircularProgress } from '@mui/material'
import InboxIcon from '@mui/icons-material/Inbox'
import { stepperStyles } from '../styles'
import { useApi } from '../../../../hooks/useApi'
import { WorkOrder } from '../../../../interfaces'
import { LoadingButton } from '../../../../components/_EOS/Button/LoadingButton'
import { Status } from '../../../../hooks/useFetchState'
import { useUser } from '../../../../hooks/useUser'
import { StepProps } from '../../../../components/_WMS/Stepper'
import { eInventoryCheckSteps, SharedStateType } from '../../types'
import { ApiResponse } from '../../../../typings/ApiResponse'
import { InventoryCheckCard, InventoryCheckCardItem, StepLoader } from '../../components'
import {
  getInventoryCheckUserDetail,
  getUserAssignedChecks,
  getAvailablePositions,
  getTakenPositions
} from '../../api'

interface ValidateInventoryWorkOrderProps extends StepProps<SharedStateType> {}

export const ValidateInventoryWorkOrder: React.FC<
  ValidateInventoryWorkOrderProps
> = ({ next, setSharedState, goToStep }) => {
  const { state, callApi } = useApi<WorkOrder>()
  const { state: assignedState, callApi: callAssignedApi } =
    useApi<ApiResponse<InventoryCheckCardItem>>()
  const { callApi: callAvailableApi } = useApi<ApiResponse<any>>()
  const { callApi: callTakenApi } = useApi<ApiResponse<any>>()

  const user = useUser()
  const inputRef = useRef<HTMLInputElement>(null)

  const [inventoryCheckId, setInventoryCheckId] = useState<number | undefined>(undefined)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  // null = verificación en curso, Set = IDs con posiciones disponibles
  const [availableIds, setAvailableIds] = useState<Set<number> | null>(null)

  const toggleExpanded = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id))
  }

  useEffect(() => {
    if (user.userDataState) {
      const userId = user.userDataState.userData?.userId
      setSharedState((prevState) => ({
        ...prevState,
        user: user.userDataState.userData
      }))
      if (userId) {
        callAssignedApi(getUserAssignedChecks(userId)).catch(() => undefined)
      }
    }
  }, [user.userDataState])

  // Verifica en paralelo qué conteos asignados tienen posiciones disponibles
  useEffect(() => {
    const items = assignedState.data?.items ?? []
    if (assignedState.status !== Status.Success) return
    if (items.length === 0) {
      setAvailableIds(new Set())
      return
    }

    const userId = user?.userDataState?.userData?.userId || 0
    setAvailableIds(null) // verificando...

    Promise.all(
      items.map(async (item) => {
        try {
          const takenRes = await getTakenPositions(item.inventoryCheckId, userId, 1)
          if ((takenRes.data?.items?.length ?? 0) > 0) return item.inventoryCheckId

          const availRes = await getAvailablePositions(item.inventoryCheckId, userId, 1)
          if ((availRes.data?.items?.length ?? 0) > 0) return item.inventoryCheckId

          return null
        } catch {
          return item.inventoryCheckId // ante error, mostrar el ítem
        }
      })
    ).then((results) => {
      setAvailableIds(new Set(results.filter((id): id is number => id !== null)))
    })
  }, [assignedState.status, assignedState.data])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    setSharedState((prev) => ({
      ...prev,
      stepTitle: 'Ingresar Conteo',
      hideBackButton: true
    }))
    return () => {
      setSharedState((prev) => ({
        ...prev,
        stepTitle: undefined,
        hideBackButton: false
      }))
    }
  }, [])

  const requestGetWorkOrder = (id?: number) => {
    const targetId = id ?? inventoryCheckId
    if (!targetId) return

    const userId = user?.userDataState.userData?.userId || 0

    callApi(getInventoryCheckUserDetail(targetId, userId))
      .then((response: WorkOrder) => {
        setSharedState((prev) => ({ ...prev, workOrderId: targetId }))
        if (!response) throw Error('WorkOrder no existe')
      })
      .then(next)
      .catch((ex) => {
        setSharedState((prev) => ({ ...prev, workOrderId: undefined, error: ex }))
        goToStep(eInventoryCheckSteps.Error)
      })
  }

  const handleSelectAssigned = async (id: number) => {
    const userId = user?.userDataState?.userData?.userId || 0
    try {
      const taken = await callTakenApi(getTakenPositions(id, userId, 1))
      const hasTaken = (taken?.items?.length ?? 0) > 0

      if (!hasTaken) {
        const available = await callAvailableApi(getAvailablePositions(id, userId, 1))
        const hasAvailable = (available?.items?.length ?? 0) > 0
        if (!hasAvailable) {
          setSharedState((prev) => ({
            ...prev,
            workOrderId: undefined,
            error:
              'No tienes posiciones disponibles en este conteo. Todas las ubicaciones están asignadas a otros usuarios o ya fueron auditadas.'
          }))
          goToStep(eInventoryCheckSteps.Error)
          return
        }
      }

      requestGetWorkOrder(id)
    } catch {
      requestGetWorkOrder(id)
    }
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    requestGetWorkOrder()
  }

  const isLoading = state.status === Status.Loading

  return (
    <Box sx={stepperStyles.root}>
      {isLoading && <StepLoader message='Buscando conteo...' />}
      <Box
        sx={stepperStyles.content}
        style={{ justifyContent: 'flex-start', paddingTop: 24, overflowY: 'auto' }}
      >
        <Box style={{ width: '100%', padding: '0 16px' }}>         
          <Box
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 16,
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
            }}
          >
            <Typography
              variant='caption'
              style={{
                color: '#999',
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: 'uppercase'
              }}
            >
              Buscar Conteo
            </Typography>

            <form onSubmit={handleSubmit}>
              <TextField
                inputRef={inputRef}
                type='number'
                variant='outlined'
                placeholder='Ingresa nro. de conteo'
                value={inventoryCheckId ?? ''}
                onChange={(e) =>
                  setInventoryCheckId(
                    e.target.value ? parseInt(e.target.value) : undefined
                  )
                }
                fullWidth
                size='small'
                style={{ marginTop: 8, marginBottom: 12 }}
              />
            </form>

            <LoadingButton
              loading={isLoading}
              variant='contained'
              color='primary'
              onClick={() => requestGetWorkOrder()}
              fullWidth
              disabled={!inventoryCheckId}
              style={{ borderRadius: 24, textTransform: 'none', fontWeight: 700, marginBottom: 12 }}
            >
              Buscar
            </LoadingButton>

            <LoadingButton
              loading={false}
              variant='outlined'
              color='primary'
              fullWidth
              onClick={() => goToStep(eInventoryCheckSteps.AllCyclicals)}
              style={{
                borderRadius: 24,
                textTransform: 'none'
              }}
            >
              No conozco el nro. de conteo
            </LoadingButton>
          </Box>

          <Box style={{ marginTop: 24 }}>
            <Typography
              variant='caption'
              style={{
                color: '#999',
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: 'uppercase'
              }}
            >
              Mis Asignados
            </Typography>

            {assignedState.status === Status.Loading && (
              <Box
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: 16
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}

            {assignedState.status === Status.Success && (
              <Box style={{ marginTop: 8 }}>
                {/* Verificando disponibilidad de posiciones */}
                {availableIds === null && (
                  <Box style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}

                {/* Sin conteos con posiciones disponibles */}
                {availableIds !== null && availableIds.size === 0 && (
                  <Box
                    style={{
                      background: '#fff',
                      borderRadius: 12,
                      padding: '24px 16px',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 8,
                      marginTop: 8
                    }}
                  >
                    <InboxIcon style={{ fontSize: 40, color: '#cbd5e1' }} />
                    <Typography variant='body2' style={{ fontWeight: 700, color: '#374151' }}>
                      Sin conteos asignados
                    </Typography>
                    <Typography variant='caption' style={{ color: '#9ca3af', textAlign: 'center' }}>
                      No tienes conteos activos asignados en este momento
                    </Typography>
                  </Box>
                )}

                {/* Solo conteos con posiciones disponibles */}
                {availableIds !== null &&
                  (assignedState.data?.items ?? [])
                    .filter((item) => availableIds.has(item.inventoryCheckId))
                    .map((item, index) => (
                      <InventoryCheckCard
                        key={item.inventoryCheckId}
                        item={item}
                        index={index}
                        isExpanded={expandedId === item.inventoryCheckId}
                        onToggle={() => toggleExpanded(item.inventoryCheckId)}
                        onSelect={() => handleSelectAssigned(item.inventoryCheckId)}
                        disabled={isLoading}
                      />
                    ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
